import { useEffect, useState } from "react";
import axios from "axios";
import { Video, Star, Quote, UploadCloud } from "lucide-react";
import { TestimonialUploadModal } from "@/components/site/TestimonialUploadModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Public grid of approved video testimonials.
 * Always visible on the homepage — shows an empty-state upload CTA
 * when no testimonials have been approved yet.
 */
export const TestimonialsGallery = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/testimonials`)
      .then((r) => {
        if (mounted) setItems(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (mounted) setItems([]);
      })
      .finally(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      data-testid="testimonials-gallery"
      className="relative overflow-hidden py-16 md:py-24 border-y-2 border-red-600"
      style={{
        background:
          "radial-gradient(circle at 25% 30%, rgba(30,58,138,0.45) 0%, transparent 60%), radial-gradient(circle at 75% 70%, rgba(220,38,38,0.25) 0%, transparent 60%), linear-gradient(180deg, #020617 0%, #0b1220 60%, #020617 100%)",
      }}
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-[11px] uppercase tracking-[0.4em] text-sky-300 font-bold flex items-center justify-center gap-2 mb-3">
            <Star size={13} className="text-red-500 fill-red-500" />
            Real Homeowners · Real Stories
            <Star size={13} className="text-red-500 fill-red-500" />
          </div>
          <h2 className="font-impact uppercase text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            Watch Neighbors <br className="sm:hidden" />
            <span className="text-red-500">Talk About JD.</span>
          </h2>
          <p className="text-slate-300 text-base sm:text-lg mt-5 leading-relaxed">
            Every roof tells a story. If we worked on yours, drop a quick video and
            let other homeowners see what it's like to work with Storm Damage Pros.
          </p>
        </div>

        {/* Grid or empty state */}
        {loaded && items.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center">
            <div className="border-2 border-dashed border-sky-400/50 p-10 md:p-14 bg-slate-900/50">
              <Quote size={38} className="text-sky-300 mx-auto mb-4" />
              <div className="font-impact uppercase text-white text-2xl md:text-3xl tracking-tight">
                Be the first to share.
              </div>
              <p className="text-slate-300 mt-3 mb-6">
                Record a quick 30-second video on your phone, upload it here, and
                JD will publish it after a quick review.
              </p>
              <TestimonialUploadModal>
                <button
                  type="button"
                  data-testid="empty-upload-cta"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-impact uppercase tracking-wider text-sm px-6 py-3.5 border-2 border-white transition-all hover:-translate-y-0.5"
                >
                  <UploadCloud size={16} /> Upload Your Video
                </button>
              </TestimonialUploadModal>
            </div>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((t) => (
                <article
                  key={t.id}
                  data-testid={`testimonial-${t.id}`}
                  className="group bg-slate-900/70 border-2 border-white/10 hover:border-sky-400 transition-all overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-video bg-black">
                    <video
                      src={`${API}/testimonials/${t.id}/video`}
                      controls
                      preload="metadata"
                      className="w-full h-full object-contain bg-black"
                    />
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 flex items-center gap-1">
                      <Video size={11} /> Verified
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-1 mb-2">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} size={13} className="text-red-500 fill-red-500" />
                      ))}
                    </div>
                    <div className="font-impact uppercase text-white text-xl tracking-tight leading-none">
                      {t.name}
                    </div>
                    {(t.city || t.damage_type) && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300 font-bold mt-1">
                        {t.city}
                        {t.city && t.damage_type ? " · " : ""}
                        {t.damage_type ? `${t.damage_type} damage` : ""}
                      </div>
                    )}
                    {t.message && (
                      <p className="text-slate-300 text-sm mt-3 leading-relaxed">
                        “{t.message}”
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>

            {/* Add-your-own CTA below the grid */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
              <div className="text-slate-300 text-sm sm:text-base">
                Been on our roof? Share your story so others can trust us too.
              </div>
              <TestimonialUploadModal>
                <button
                  type="button"
                  data-testid="grid-upload-cta"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-impact uppercase tracking-wider text-sm px-6 py-3 border-2 border-white transition-all hover:-translate-y-0.5"
                >
                  <UploadCloud size={16} /> Upload Your Video
                </button>
              </TestimonialUploadModal>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
